import React, { useEffect, useState } from "react";
import ToastContent from "../common/ToastContent";
import { Account } from "react-aad-msal";
import { connect } from "react-redux";
import { Delete } from "../../api/apiUtils";
import { ErrorResponse, SofView } from "../../models";
import { DefaultButton, mergeStyleSets, PrimaryButton } from "office-ui-fabric-react";
import { mergeStyles, FontIcon } from "@fluentui/react";
import { last6Digits } from "../../services/Helper";
import { State } from "../../store";
import { SofProcessor } from "../../services/SofProcessor";
import { toast } from "react-toastify";
import { UndoResult } from "../../models/UndoResult";
import { useHistory } from "react-router";
import * as ActivityLogStore from "../../store/ActivityLogsStore";
import * as GeneralStore from "../../store/GeneralStore";
import * as LineHandlingStore from "../../store/LineHandlingStore";
import * as OfflineDataStore from "../../store/OfflineDataStore";
import * as SofViewStore from "../../store/SofViewStore";

type Props = ActivityLogStore.ActivityLogsState &
  GeneralStore.GeneralState &
  LineHandlingStore.LineHandlingState &
  OfflineDataStore.OfflineDataState &
  typeof ActivityLogStore.actionCreators &
  typeof LineHandlingStore.actionCreators &
  typeof OfflineDataStore.actionCreators &
  typeof SofViewStore.actionCreators & {
    account: Account;
  };

const buttonBase = mergeStyles({
  padding: "9px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
});
const buttonIcon = mergeStyles({
  padding: "9px 10px",
  height: 38,
  width: 38,
  justifyContent: "center",
  border: "1px solid var(--grey-200)",
  display: "flex",
  alignItems: "center",
  color: "#4c4c4c",
  background: "var(--grey-050)",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
});
const classes = mergeStyleSets({
  header: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 24,
        color: "#0F7F79",
    textAlign: "left"
  },
  body: {
    textAlign: "left",
    margin: "18px 0 38px 0"
  },
  footer: {
    textAlign: "right"
  },
  button: [
    buttonBase,
    {
      padding: "10px 16.5px",
      height: "auto"
    }
  ],
  modalButton: [
    buttonBase,
    {
      padding: "9px 20px"
    }
  ],
  primaryButton: [
    buttonBase,
    {
      backgroundColor: "#009FDF",
      color: "#FFFFFF",
      padding: "9px 20px"
    }
  ]
});

const UndoModal: React.FC<Props> = (props: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lineHandlingNo, setLineHandlingNo] = useState("");
  const [message, setMessage] = useState("");
  const [nomMessage, setNomMessage] = useState("");
  const [disableUndoButton, setDisableButton] = useState(true);
  const [canUndo, setCanUndo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const history = useHistory();

  const undoClick = () => {
    setShowModal(true);
  };

  const undoLastAction = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      let result: UndoResult;
      if (lineHandlingNo === undefined) return;
      const offlineData = props.offlineData.find(
        (f) => f.lineHandlingNo.toLowerCase() === lineHandlingNo.toLowerCase()
      );
      if (offlineData === undefined) {
        const response = await Delete(
          `api/v1.5/linehandling/${lineHandlingNo}/activities/undo?correlationId=${props.correlationId}&isExtended=${props.isMarineUndoExtendedAllowed}`
        );
        setShowModal(false);
        setIsLoading(false);
        if (!response.ok) {
          let errorMessage = (await response.json()) as ErrorResponse;
          toast.error(
            <ToastContent
              title="Error in Undo"
              content={errorMessage.message}
            />,
            {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined
            }
          );
          return;
        }
        result = (await response.json()) as UndoResult;
      } else {
        result = await SofProcessor.undoAsync(lineHandlingNo);
        setShowModal(false);
        setIsLoading(false);
        if (offlineData) {
          const actions = offlineData.actions.filter(
            (f) => f.executionTime !== result.logTime
          );
          if (actions.length > 0) {
            const newOfflineData = {
              lineHandlingNo,
              actions
            };
            props.updateOfflineData(newOfflineData);
          } else {
            props.removeOfflineData(lineHandlingNo);
          }
        }
      }

      if (result) {
        props.getActivityLogs(result.lineHandlingNo, () => {
          props.getLineHandling(
            lineHandlingNo,
            result.nominationNo,
            false,
            false,
            () => {
              let previousPage = `/marine/${result.lineHandlingNo}/sof`;
              if (result.nominationNo) {
                previousPage += `?nomination=${result.nominationNo}`;
              }
              props.setSofView(
                result.nominationNo ? SofView.Nomination : SofView.LineHandling
              );
              if (history.location.search !== previousPage) {
                history.push(previousPage);
              }
            }
          );
        });
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (props.lineHandling.result) {
      setLineHandlingNo(props.lineHandling.result.lineHandlingNo);

      setIsRejected(props.lineHandling.result.isRejected);
    }
  }, [props.lineHandling]);

  useEffect(() => {
    if (props.activityLogs.result) {
      const userActivities = props.activityLogs.result.filter(
        (f) =>
          (!props.isOffline && f.performedBy !== undefined) ||
          (props.isOffline && f.offline)
      );
      setDisableButton(userActivities.length === 0);
    }
  }, [props.activityLogs, props.isOffline]);

  useEffect(() => {
    const { activityLogs, account } = props;
    if (activityLogs.result && account && showModal) {
      const lastAction = activityLogs.result
        .sort((a, b) => b.id - a.id)
        .find((f) => f.performedBy === account.accountIdentifier);
      if (lastAction) {
        const hasActivitiesAfter = activityLogs.result.filter(
          (f) =>
            f.id > lastAction.id &&
            f.nominationNo === lastAction.nominationNo &&
            f.performedBy !== undefined &&
            f.performedBy !== account.accountIdentifier
        );
        if (hasActivitiesAfter.length > 0) {
          setCanUndo(false);
          setMessage("");
          setNomMessage("");
        } else {
          setCanUndo(true);
          setMessage(lastAction.description);
          if (lastAction.nominationNo) {
            setNomMessage(`Nomination ${last6Digits(lastAction.nominationNo)}`);
          }
        }
      } else {
        setCanUndo(false);
        setMessage("");
        setNomMessage("");
      }
    }
  }, [showModal]);

  return (
    <>
      {props.isMarineEditActivityAllowed && (
        <button
          id="btn-undo"
          className={buttonIcon}
          onClick={undoClick}
          disabled={disableUndoButton || isRejected}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.8565 5.83333L6.96984 7.94667L5.7915 9.125L1.6665 5L5.7915 0.875L6.96984 2.05333L4.8565 4.16667H10.8332C12.6013 4.16667 14.297 4.86905 15.5472 6.11929C16.7975 7.36953 17.4998 9.06522 17.4998 10.8333C17.4998 12.6014 16.7975 14.2971 15.5472 15.5474C14.297 16.7976 12.6013 17.5 10.8332 17.5H3.33317V15.8333H10.8332C12.1593 15.8333 13.431 15.3066 14.3687 14.3689C15.3064 13.4312 15.8332 12.1594 15.8332 10.8333C15.8332 9.50725 15.3064 8.23548 14.3687 7.2978C13.431 6.36012 12.1593 5.83333 10.8332 5.83333H4.8565Z" 
              fill="#353B3B"/>
          </svg>
        </button>
      )}
      {showModal && (
        <div className="modal-bg">
          <div className="modal-right" style={{ width: 400 }}>
            <form>
              <div className={classes.header}>Undo Activity</div>
              <div className={classes.body}>
                {canUndo && (
                  <span>
                    You are about to undo your last activity,&nbsp;
                    <b id="lbl-last-activity">{message}</b>
                    {nomMessage && (
                      <>
                        &nbsp;in <b id="lbl-last-nomination">{nomMessage}</b>
                      </>
                    )}
                    . Do you want to proceed?
                  </span>
                )}
                {!canUndo && (
                  <span>
                    You cannot perform undo at this moment. The last activity
                    was performed by other user.
                  </span>
                )}
              </div>
              <div className={classes.footer}>
                              {canUndo && (
                    
                              <PrimaryButton
                                      id="btn-submit-undo"
                                      type="submit" 
                    onClick={undoLastAction}
                                  disabled={isLoading}
                                  text="Proceed"
                              />
                  //<button
                  //  id="btn-submit-undo"
                  //  className={`${classes.primaryButton} btn-prim`}
                  //  type="submit"
                  //  onClick={undoLastAction}
                  //  disabled={isLoading}
                  //>
                  //  Proceed
                  //</button>
                )}
                              &nbsp;

                              <DefaultButton
                                  onClick={() => setShowModal(false)}
                                  disabled={isLoading}
                                  text="Cancel"
                              />
                {/*<button*/}
                {/*  id="btn-cancel-undo"*/}
                {/*  className={`${classes.modalButton} btn-def`}*/}
                {/*  type="button"*/}
                {/*  onClick={() => setShowModal(false)}*/}
                {/*  disabled={isLoading}*/}
                {/*>*/}
                {/*  Cancel*/}
                {/*</button>*/}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
export default connect(
  (state: State) => ({
    ...state.activityLogs,
    ...state.authentication,
    ...state.general,
    ...state.lineHandling,
    ...state.offlineData
  }),
  {
    ...ActivityLogStore.actionCreators,
    ...LineHandlingStore.actionCreators,
    ...OfflineDataStore.actionCreators,
    ...SofViewStore.actionCreators
  }
)(UndoModal as any);
